import { IframeMessageType } from '../hooks/useIframe/useIframe.constants';
import Utils from './Utils';

const AUTH_CACHE_RESPONSE_TIMEOUT_MS = 2000;
const AUTH_CACHE_RETRY_ATTEMPTS = 2;
// Once every attempt of a full-budget call has failed, Layout's own session
// is almost certainly gone too (it can't relay what it doesn't have) - skip
// straight to null for a short window instead of making every caller that
// fails around the same moment (e.g. a burst of API calls once the relayed
// token expires) each pay the full retry*timeout budget again. This must
// never be set by the boot-time probe (see index.tsx) - its single
// short-timeout attempt losing the race against Layout's listener becoming
// ready is a much weaker signal, and would otherwise starve the real,
// full-budget callers (acquireToken, refreshAccessToken) for the next
// AUTH_CACHE_COOLDOWN_MS.
const AUTH_CACHE_COOLDOWN_MS = 5000;
// auth0-spa-js's own CacheManager applies this same leeway before treating a
// cached access token as expired (see its silent-token-fetch path) - without
// it, a token can be "valid" here for up to 60s after the SDK itself would
// already refuse to use it, so a caller recovering from a login_required
// error sees hasValidCachedSession() report a token that can't actually
// satisfy the request that just failed, and gives up instead of relaying.
const CACHE_EXPIRY_LEEWAY_MS = 60000;

export const isEmbeddedInIframe = (): boolean => window.self !== window.top;

// The exact parent origin isn't reliably known across environments (Layout
// can be reached from more than one host), but document.referrer is set by
// the browser to the embedding page and is the only origin a postMessage
// here can legitimately need to reach.
export const getParentOrigin = (): string =>
  document.referrer ? new URL(document.referrer).origin : '*';

export const getCacheKeyPrefix = (): string | null => {
  const clientId = Utils.getAuth0ClientIdByEnvironment('clientid');
  const audience = Utils.getAuth0ClientIdByEnvironment('audience');
  if (!clientId || !audience) return null;
  return `@@auth0spajs@@::${clientId}::${audience}::`;
};

const findCachedEntry = (): { key: string; entry: any } | null => {
  const prefix = getCacheKeyPrefix();
  if (!prefix) return null;
  const key = Object.keys(localStorage).find((k) => k.startsWith(prefix));
  if (!key) return null;
  try {
    return { key, entry: JSON.parse(localStorage.getItem(key) || '') };
  } catch (e) {
    return null;
  }
};

// A previously relayed entry has no refresh token (see the response handler
// below - Layout strips it before sending) and stops being usable once its
// access token expires - an existence-only check would then permanently
// skip re-requesting on every future call, since a dead key would still be
// sitting there.
const getValidCachedAccessToken = (): string | null => {
  const found = findCachedEntry();
  const expiresAt = found && found.entry ? found.entry.expiresAt : undefined;
  if (
    typeof expiresAt !== 'number' ||
    expiresAt * 1000 - CACHE_EXPIRY_LEEWAY_MS <= Date.now()
  ) {
    return null;
  }
  const token =
    found && found.entry && found.entry.body
      ? found.entry.body.access_token
      : undefined;
  return typeof token === 'string' ? token : null;
};

const hasValidCachedSession = (): boolean => getValidCachedAccessToken() !== null;

// auth0-spa-js's own checkSession() (invoked by Auth0Provider on mount, and
// internally ahead of each silent refresh) is gated on this plain,
// JS-readable cookie - it has no way to know we just seeded its localStorage
// cache out-of-band, and silently no-ops without it. path=/ must be
// explicit: document.cookie otherwise defaults to the current route's path,
// which could scope it away from where checkSession() runs.
const seedIsAuthenticatedCookie = (): void => {
  const clientId = Utils.getAuth0ClientIdByEnvironment('clientid');
  if (!clientId) return;
  const secureAttrs =
    window.location.protocol === 'https:' ? '; Secure; SameSite=None' : '';
  document.cookie = `auth0.${clientId}.is.authenticated=true; path=/${secureAttrs}`;
};

const writeCacheEntry = (key: unknown, entry: unknown): boolean => {
  if (typeof key !== 'string' || !entry) return false;
  try {
    localStorage.setItem(key, JSON.stringify(entry));
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

// Multiple callers can fail around the same moment once the relayed access
// token expires (a mount-time refresh racing an in-flight API call) - share
// one in-flight relay instead of each firing its own REQUEST_AUTH_CACHE and
// independently waiting out the timeout. Keyed by timeoutMs/attempts so a
// short-budget caller (the boot-time probe in index.tsx) never shares an
// outcome with a longer-budget caller (acquireToken's default retry budget).
const inFlightRequests = new Map<string, Promise<string | null>>();
let cooldownUntil = 0;

const sendOneRequest = (timeoutMs: number): Promise<string | null> => {
  return new Promise<string | null>((resolve) => {
    let settled = false;

    const finish = (accessToken: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', handleMessage);
      resolve(accessToken);
    };

    const handleMessage = (event: MessageEvent) => {
      // The exact parent origin isn't reliably known across environments,
      // but a genuine reply can only ever come from window.parent itself.
      if (event.source !== window.parent) return;
      if (!event.data || event.data.type !== IframeMessageType.AUTH_CACHE_RESPONSE) return;

      const {
        cacheKey,
        cacheEntry,
        idTokenCacheKey,
        idTokenCacheEntry,
        accessToken,
      } = event.data;
      const seeded = writeCacheEntry(cacheKey, cacheEntry);
      // auth0-spa-js keeps getUser()'s ID-token claims in a second cache
      // entry at a key with no audience/scope segment - write it too when
      // Layout sends it, so isAuthenticated/getUser() are correct
      // immediately, not just getAccessTokenSilently().
      writeCacheEntry(idTokenCacheKey, idTokenCacheEntry);
      if (seeded) seedIsAuthenticatedCookie();
      if (typeof accessToken === 'string') {
        localStorage.setItem('auth0offline_access', accessToken);
      }

      finish(typeof accessToken === 'string' ? accessToken : null);
    };

    window.addEventListener('message', handleMessage);
    window.parent.postMessage(
      { type: IframeMessageType.REQUEST_AUTH_CACHE },
      getParentOrigin()
    );
    setTimeout(() => finish(null), timeoutMs);
  });
};

const requestFreshAuthCache = (
  timeoutMs: number,
  attempts: number,
  entersCooldownOnFailure: boolean
): Promise<string | null> => {
  const key = `${timeoutMs}:${attempts}`;
  const existing = inFlightRequests.get(key);
  if (existing) return existing;

  const request = (async () => {
    if (Date.now() < cooldownUntil) return null;

    for (let attempt = 1; attempt <= attempts; attempt++) {
      const token = await sendOneRequest(timeoutMs);
      if (token) return token;

      // Layout can still reply after this attempt's own timeout fires (slow
      // frame, backgrounded tab) - if a cache entry is valid by now, treat
      // it as recovered rather than spending another round trip or, on the
      // last attempt, declaring failure the cache itself disagrees with.
      const recovered = getValidCachedAccessToken();
      if (recovered) return recovered;
    }

    if (entersCooldownOnFailure) {
      cooldownUntil = Date.now() + AUTH_CACHE_COOLDOWN_MS;
    }
    return null;
  })().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, request);
  return request;
};

/**
 * IMAS is embedded read-only inside the Phantom Layout iframe and must never
 * hold its own refresh token (see IntegrityConnectIframe's relay, which
 * strips it before sending) - Layout is the single origin that rotates it
 * and is the only one that can fall back to a real top-level login when its
 * own session finally expires. This asks Layout to mint and relay a fresh
 * access token, seeding both localStorage cache entries and the
 * is.authenticated cookie on success.
 *
 * Used two ways: at boot, before the Auth0 provider mounts (see index.tsx),
 * so the SDK reads a seeded cache on its very first init instead of needing
 * a redirect; and again from Auth0Authenticator/App.tsx whenever a token
 * fetch fails mid-session, so an expired relayed access token (it carries no
 * refresh token) recovers with a fresh one from Layout instead of forcing a
 * logout that would lose in-progress work.
 *
 * Resolves the fresh access token when Layout responds, or null if there's
 * nothing to do (not embedded, already-valid cache) or Layout never replies.
 *
 * `entersCooldownOnFailure` defaults to true for the real recovery budget
 * (acquireToken, refreshAccessToken). The boot-time call in index.tsx passes
 * false: its single short-timeout attempt losing the race against Layout's
 * listener becoming ready isn't a reliable signal that Layout has no
 * session to relay, so it must not lock out the full-budget callers that
 * run moments later.
 */
export const requestAuthCacheFromParent = (
  timeoutMs = AUTH_CACHE_RESPONSE_TIMEOUT_MS,
  attempts = AUTH_CACHE_RETRY_ATTEMPTS,
  entersCooldownOnFailure = true
): Promise<string | null> => {
  if (!isEmbeddedInIframe() || hasValidCachedSession()) {
    return Promise.resolve(null);
  }
  return requestFreshAuthCache(timeoutMs, attempts, entersCooldownOnFailure);
};

/**
 * Sends a LOGOUT message up to Layout instead of attempting an in-frame
 * Auth0 redirect. A top-level loginWithRedirect() cannot complete inside
 * this iframe - Universal Login refuses to be framed, and silent auth needs
 * a third-party cookie unavailable in this nested context. Layout is a real
 * top-level browsing context, so it's the only place a login redirect can
 * actually succeed; once it does, this iframe's own boot-time seeding picks
 * the fresh session back up automatically.
 */
export const notifyParentToLogin = (): void => {
  try {
    window.parent.postMessage(
      { type: IframeMessageType.LOGOUT, message: window.location.pathname },
      getParentOrigin()
    );
  } catch (e) {
    console.error(e);
  }
};
